import {
  InputGroup,
  InputLeftAddon,
  Box,
  Flex,
  Input,
  InputRightElement,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';

import { DateRangePicker } from 'components/Datepicker';
import { ControllableDrawer } from 'components/Drawer';
import ReactSelect, { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import FilterEmployeeAndGroupList from 'containers/Scheduling/RightDrawer/FilterEmployeeAndGroupList';
import { roles } from 'global/rolesAndPermissions';
import { fetchPaginatedCategory } from 'hooks/timeOff/useFetchPaginatedCategories';
import { getAsyncSelectLoadOptions, getUserStore } from 'utils/helpers';

import { timeOffRequestStatus } from '../options';

interface IFilterGroupProps {
  handleDateChange?: (selectedDates: any) => void;
  handleSearchInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmployeeFilterFormSubmit: (values: any) => void;
  handleCategoryChange: (values: any) => void;
  handleTimeOffStatusChange?: (value: any) => void;
  searchPlaceholder?: string;
  searchQuery?: any;
}

const CustomValueContainer: React.FC<any> = ({ children, hasValue, ...props }) => {
  if (!hasValue) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const CHIPS_LIMIT = 1;
  const [chips, otherChildren] = children;
  const overflowCounter = chips.slice(CHIPS_LIMIT).length;
  const displayChips = chips.slice(overflowCounter, overflowCounter + CHIPS_LIMIT);

  return (
    <components.ValueContainer {...props} display="block">
      {displayChips}
      {overflowCounter > 0 && <span style={{ minWidth: '30px', marginLeft: '4px' }}>+ {overflowCounter}</span>}
      {otherChildren}
    </components.ValueContainer>
  );
};

const FilterGroup: React.FC<IFilterGroupProps> = ({
  handleSearchInputChange,
  handleDateChange,
  handleEmployeeFilterFormSubmit,
  handleCategoryChange,
  handleTimeOffStatusChange,
  searchPlaceholder,
  searchQuery,
}) => {
  const selectRef = useRef<any>(null);

  const [employeeListFormData, setEmployeeListFormData] = useState({ user_ids: [] as any, group_ids: [] as any });

  const userStore = getUserStore();

  const { t } = useTranslation(['timeOff', 'common']);
  const {
    isOpen: isEmployeeListDrawerOpen,
    onOpen: onEmployeeListDrawerOpen,
    onClose: onEmployeeListDrawerClose,
  } = useDisclosure();

  useEffect(() => {
    /**
     * Hack to close the category dropdown when user clicks outside
     */
    const handleFocusChange = () => {
      const active = document.activeElement;

      if (selectRef?.current?.state?.menuIsOpen && !active?.closest('.timeero-select__input')) {
        selectRef.current.onMenuClose();
      }
    };

    window.addEventListener('focusin', handleFocusChange);
    return () => {
      window.removeEventListener('focusin', handleFocusChange);
    };
  }, []);

  const dateRange = useMemo(() => {
    return searchQuery?.date_range
      ? searchQuery?.date_range.split(',').map((item: string) => (item !== '' ? new Date(item) : null))
      : [null, null];
  }, [searchQuery]);

  const loadCategoryOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedCategory, {
      page,
      search,
      active: '1',
    });
  };

  const handleEmployeeAndGroupListSubmit = (values: any) => {
    onEmployeeListDrawerClose();
    setEmployeeListFormData(values);
    handleEmployeeFilterFormSubmit(values.user_ids);
  };

  const handleUserSelectClick = () => {
    onEmployeeListDrawerOpen();
  };

  return (
    <>
      <Flex flexWrap="wrap" gridGap="2" flexDirection={{ base: 'column', sm: 'row' }}>
        {handleTimeOffStatusChange ? (
          <InputGroup maxWidth={{ base: '100%', sm: '210px' }} height="fit-content">
            <InputLeftAddon children="Status" px="2" />
            <ReactSelect
              options={timeOffRequestStatus}
              size="md"
              fontSize="sm"
              isSearchable
              defaultValue={{ label: 'All', value: 'All' }}
              placeholder="Select Status"
              onChange={handleTimeOffStatusChange}
            />
          </InputGroup>
        ) : null}

        {userStore?.userInfo?.roleId !== roles.EMPLOYEE ? (
          <InputGroup maxWidth={{ base: '100%', sm: '210px' }}>
            <InputLeftAddon children="Users" fontSize="sm" />
            <Input
              placeholder={t('common:selectUsers')}
              fontSize="sm"
              value={`${employeeListFormData.user_ids.length || t('common:all')} ${t('common:users')}`}
              onChange={() => null}
              color={employeeListFormData.user_ids.length ? 'primary.900' : 'gray.400'}
              pr="60px"
            />
            <InputRightElement
              width="60px"
              children={
                <Button size="xs" onClick={handleUserSelectClick}>
                  {t('common:select')}
                </Button>
              }
            />
          </InputGroup>
        ) : null}

        <InputGroup maxWidth={{ base: '100%', sm: '320px' }}>
          <InputLeftAddon children="Category" fontSize="sm" />
          <ReactSelectAsyncPaginate
            selectRef={selectRef}
            loadOptions={loadCategoryOptions}
            onChange={handleCategoryChange}
            additional={{
              page: 1,
            }}
            size="md"
            variant="outline"
            fontSize="sm"
            isClearable
            isSearchable
            isMulti
            placeholder="Select Category"
            debounceTimeout={300}
            hasCheckbox
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
            components={{ ValueContainer: CustomValueContainer }}
            styles={{ valueContainer: (provided: any) => ({ ...provided, display: 'flex' }) }}
            classNamePrefix="timeero-select"
          />
        </InputGroup>

        {handleDateChange ? (
          <Box maxWidth={{ base: '100%', md: '270px' }} width="100%">
            <DateRangePicker dateRange={dateRange} onChange={handleDateChange} />
          </Box>
        ) : null}

        <Box ml="auto" maxWidth={{ md: '250px' }} width="100%">
          <SearchField
            inputProps={{
              placeholder: searchPlaceholder,
              onChange: handleSearchInputChange,
            }}
          />
        </Box>
      </Flex>

      <ControllableDrawer isOpen={isEmployeeListDrawerOpen} onClose={onEmployeeListDrawerClose}>
        <Formik enableReinitialize initialValues={employeeListFormData} onSubmit={handleEmployeeAndGroupListSubmit}>
          {() => (
            <Form noValidate autoComplete="off">
              <FilterEmployeeAndGroupList />
            </Form>
          )}
        </Formik>
      </ControllableDrawer>
    </>
  );
};

export default memo(FilterGroup);
